import { Business, User } from "@/types";

/**
 * Centralized localStorage management with namespaced keys
 */
class StorageManager {
  // Storage key definitions grouped by domain
  private readonly keys = {
    auth: {
      accessToken: "auth.accessToken",
      refreshToken: "auth.refreshToken",
      user: "auth.user",
    },
    business: {
      list: "business.list",
      current: "business.current",
    },
    preferences: {
      theme: "preferences.theme",
      language: "preferences.language",
      sidebar: "preferences.sidebar",
    },
  } as const;

  // Check if we're on the client side
  private get isClient(): boolean {
    return typeof window !== "undefined";
  }

  // ============ AUTH METHODS ============

  setAuthTokens(accessToken: string, refreshToken: string, remember: boolean = true): void {
    if (!this.isClient) return;
    const storage = remember ? localStorage : sessionStorage;
    storage.setItem(this.keys.auth.accessToken, accessToken);
    storage.setItem(this.keys.auth.refreshToken, refreshToken);
  }

  getAccessToken(): string | null {
    if (!this.isClient) return null;
    return (
      localStorage.getItem(this.keys.auth.accessToken) ||
      sessionStorage.getItem(this.keys.auth.accessToken)
    );
  }

  getRefreshToken(): string | null {
    if (!this.isClient) return null;
    return (
      localStorage.getItem(this.keys.auth.refreshToken) ||
      sessionStorage.getItem(this.keys.auth.refreshToken)
    );
  }

  setUser(user: User, remember: boolean = true): void {
    if (!this.isClient) return;
    const storage = remember ? localStorage : sessionStorage;
    storage.setItem(this.keys.auth.user, JSON.stringify(user));
  }

  getUser(): User | null {
    if (!this.isClient) return null;
    const user =
      localStorage.getItem(this.keys.auth.user) ||
      sessionStorage.getItem(this.keys.auth.user);
    return user && user !== "undefined" ? JSON.parse(user) : null;
  }

  clearAuth(): void {
    if (!this.isClient) return;
    // Clear from both storages to be safe
    localStorage.removeItem(this.keys.auth.accessToken);
    localStorage.removeItem(this.keys.auth.refreshToken);
    localStorage.removeItem(this.keys.auth.user);
    
    sessionStorage.removeItem(this.keys.auth.accessToken);
    sessionStorage.removeItem(this.keys.auth.refreshToken);
    sessionStorage.removeItem(this.keys.auth.user);
  }

  // ============ BUSINESS METHODS ============

  setBusinesses(businesses: Business[]): void {
    if (!this.isClient) return;
    localStorage.setItem(this.keys.business.list, JSON.stringify(businesses));
  }

  getBusinesses(): Business[] | null {
    if (!this.isClient) return null;
    const businesses = localStorage.getItem(this.keys.business.list);
    return businesses ? JSON.parse(businesses) : null;
  }

  setCurrentBusiness(business?: Business | null): void {
    if (!this.isClient) return;
    localStorage.setItem(this.keys.business.current, JSON.stringify(business));
  }

  getCurrentBusiness(): Business | null {
    if (!this.isClient) return null;
    const business = localStorage.getItem(this.keys.business.current);
    return business ? JSON.parse(business) : null;
  }

  clearBusiness(): void {
    if (!this.isClient) return;
    localStorage.removeItem(this.keys.business.list);
    localStorage.removeItem(this.keys.business.current);
  }

  // ============ PREFERENCES METHODS ============

  setTheme(theme: "light" | "dark"): void {
    if (!this.isClient) return;
    localStorage.setItem(this.keys.preferences.theme, theme);
  }

  getTheme(): "light" | "dark" | null {
    if (!this.isClient) return null;
    return localStorage.getItem(this.keys.preferences.theme) as
      | "light"
      | "dark"
      | null;
  }

  setLanguage(language: string): void {
    if (!this.isClient) return;
    localStorage.setItem(this.keys.preferences.language, language);
  }

  getLanguage(): string | null {
    if (!this.isClient) return null;
    return localStorage.getItem(this.keys.preferences.language);
  }

  setSidebarState(isCollapsed: boolean): void {
    if (!this.isClient) return;
    localStorage.setItem(this.keys.preferences.sidebar, String(isCollapsed));
  }

  getSidebarState(): boolean {
    if (!this.isClient) return false;
    const state = localStorage.getItem(this.keys.preferences.sidebar);
    return state === "true";
  }

  clearPreferences(): void {
    if (!this.isClient) return;
    localStorage.removeItem(this.keys.preferences.theme);
    localStorage.removeItem(this.keys.preferences.language);
    localStorage.removeItem(this.keys.preferences.sidebar);
  }

  // ============ UTILITY METHODS ============

  /**
   * Clear all auth and business data (useful for logout)
   */
  clearUserData(): void {
    this.clearAuth();
    this.clearBusiness();
  }

  clearCurrentBusiness(): void {
    if (!this.isClient) return;
    localStorage.removeItem(this.keys.business.current);
  }

  /**
   * Clear everything from localStorage
   */
  clearAll(): void {
    if (!this.isClient) return;
    localStorage.clear();
  }

  /**
   * Get all storage keys for debugging
   */
  getAllKeys(): string[] {
    if (!this.isClient) return [];
    return Object.keys(localStorage);
  }

  /**
   * Check if user is authenticated based on stored tokens
   */
  isAuthenticated(): boolean {
    if (!this.isClient) return false;
    return !!(this.getAccessToken() && this.getUser());
  }
}

// Export singleton instance
export const storage = new StorageManager();
